import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, Copy } from "lucide-react";
import type { TenantUpdatePayload, TenantWithSensitive } from "@am/shared";
import { generatePassword } from "@am/shared";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { PageHeader } from "../components/PageHeader";
import {
  STEPS,
  formFromTenant,
  buildWidgetConfig,
  type TenantFormState,
} from "../components/tenant-form/formModel";
import { Stepper } from "../components/tenant-form/Stepper";
import { StepSection } from "../components/tenant-form/sections";

export function EditTenantPage() {
  const { id = "" } = useParams();
  const api = useApiClient();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<TenantWithSensitive | null>(null);
  const [form, setForm] = useState<TenantFormState | null>(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const t = await api<TenantWithSensitive>(`/admin/api/tenants/${id}`);
        if (!alive) return;
        setTenant(t);
        setForm(formFromTenant(t));
      } catch (err) {
        if (alive) setLoadError(err instanceof Error ? err.message : "Erreur de chargement");
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, id]);

  const update = useCallback(
    <K extends keyof TenantFormState>(key: K, value: TenantFormState[K]) => {
      setForm((f) => (f ? { ...f, [key]: value } : f));
    },
    [],
  );

  function genPassword() {
    if (!form) return;
    const pwd = generatePassword(20);
    update("password", pwd);
    update("generated_password", pwd);
  }

  async function copyKey() {
    if (!tenant) return;
    try {
      await navigator.clipboard.writeText(tenant.widget_key);
      toast.success("Clé copiée !");
    } catch {
      toast.error("Échec de la copie.");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form || !tenant || saving) return;
    setSaving(true);

    const body: TenantUpdatePayload = { widget_config: buildWidgetConfig(form) };
    if (form.name.trim()) body.name = form.name.trim();
    body.email = form.email.trim() || null;
    if (form.password) body.admin_password = form.password;
    if (form.password && form.send_credentials_email) body.send_password_email = true;
    if (form.openai_api_key.trim()) body.openai_api_key = form.openai_api_key.trim();
    if (form.llm_model) body.llm_model = form.llm_model;
    body.business_info = form.business_info.trim() || null;
    if (Number.isFinite(form.rate_limit_per_minute) && form.rate_limit_per_minute > 0) {
      body.rate_limit_per_minute = Math.trunc(form.rate_limit_per_minute);
    }
    body.wc_url = form.wc_url.trim() || null;
    if (form.wc_consumer_key.trim()) body.wc_consumer_key = form.wc_consumer_key.trim();
    if (form.wc_consumer_secret.trim()) body.wc_consumer_secret = form.wc_consumer_secret.trim();
    body.can_customize_widget = form.can_customize_widget;

    try {
      await api(`/admin/api/tenants/${tenant.id}`, { method: "PATCH", body });
      toast.success("Tenant mis à jour !");
      navigate("/tenants");
    } catch (err) {
      toast.error("Erreur : " + (err instanceof Error ? err.message : "inconnue"));
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div className="page is-active">
        <div className="warn-box" style={{ maxWidth: 600 }}>
          <AlertTriangle size={14} /> {loadError}
        </div>
      </div>
    );
  }
  if (!tenant || !form) {
    return <div className="page is-active" />;
  }

  const current = STEPS[step]!;

  return (
    <div className="page is-active">
      <PageHeader
        title={<>Modifier <em>{tenant.name}</em></>}
        subtitle="Configuration du client"
      />

      <div className="edit-toolbar">
        <button type="button" className="btn-sm btn-ghost" onClick={() => navigate("/tenants")}>
          <ArrowLeft size={14} /> Retour
        </button>
        <span className={"badge " + (tenant.llm_configured ? "badge-green" : "badge-red")} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {tenant.llm_configured ? <><Check size={12} /> Clé LLM configurée</> : <><AlertTriangle size={12} /> Aucune clé LLM</>}
        </span>
        <div className="widget-key-chip">
          <span className="widget-key-label">Clé widget</span>
          <span className="mono widget-key-val">{tenant.widget_key}</span>
          <button type="button" className="btn-sm btn-ghost" onClick={copyKey} style={{ flexShrink: 0 }}>
            <Copy size={13} />
          </button>
        </div>
      </div>

      <div className="wizard">
        <Stepper current={step} onSelect={setStep} clickable />

        <form onSubmit={onSubmit} className="wizard-panel card">
          <div className="wizard-panel-head">
            <div className="wizard-panel-num">{step + 1}</div>
            <div>
              <h2 className="wizard-panel-title">{current.title}</h2>
              <p className="wizard-panel-desc">{current.desc}</p>
            </div>
          </div>

          <div className="wizard-panel-body">
            <StepSection id={current.id} form={form} update={update} mode="edit" genPassword={genPassword} />
          </div>

          <div className="wizard-footer">
            <button
              type="button"
              className="btn-sm btn-ghost"
              onClick={() => setStep((s) => Math.max(s - 1, 0))}
              disabled={step === 0}
            >
              <ArrowLeft size={15} /> Précédent
            </button>
            <span className="wizard-progress">Étape {step + 1} / {STEPS.length}</span>
            <button type="submit" className="btn-primary wizard-next" disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
